import { Controller, useFormContext } from "react-hook-form";
import { FormHelperText, Box, Autocomplete, TextField } from "@mui/material";
import { StyledFormControl } from "./StyleInputs";
import { useMemo } from "react";
import { escapeStringHelper } from "utils/common";

const filterOptions = (options = [], state, visibleOtherOption) => {
  const input = state?.inputValue?.trim() || "";

  if (!Array.isArray(options)) return [];

  if (!input?.length) return options;

  const filtered = [];

  if (visibleOtherOption && options.some((opt) => opt.value === "other")) {
    filtered.push({ label: "Other", value: "other" });
  }

  const regex = new RegExp(escapeStringHelper(input), "igm");

  for (const opt of options) {
    if (opt?.value === "other" && visibleOtherOption) continue; // skip for now
    if (opt?.label.match(regex)) {
      filtered.push(opt);
    }
  }

  return filtered;
};

const SelectField = ({ field }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const $errorMessage = errors[field.id]?.message;
  const customErrorMessage = field.validation?.errorMessage;
  const errorMessage = $errorMessage
    ? customErrorMessage || $errorMessage
    : undefined;

  const visibleOtherOption = field.visibleOtherOption ?? true;

  const optionsLookUp = useMemo(() => {
    const lookup = {};
    const options = field?.options ?? [];
    options.forEach((option) => {
      lookup[option.value] = option;
    });
    if (visibleOtherOption) {
      lookup["other"] = { value: "other", label: "Other" };
    }
    return lookup;
  }, [field?.options, visibleOtherOption]);

  const options = useMemo(() => {
    if (!visibleOtherOption) return field.options;
    const hasOthers = field.options?.some(
      (option) => option.value && option.value.match(/^other$/i)
    );
    const defaultOthers = { value: "other", label: "Other" };
    return hasOthers
      ? field.options
      : [defaultOthers, ...(field.options || [])];
  }, [field.options, visibleOtherOption]);

  return (
    <Box sx={{ mb: 3 }}>
      <Controller
        name={field.id}
        control={control}
        defaultValue=""
        rules={{
          required: field.required ? `${field.label} is required` : false,
        }}
        render={({ field: { onChange, value, ref } }) => (
          <StyledFormControl fullWidth error={!!errorMessage}>
            <Autocomplete
              value={optionsLookUp[value] || null}
              onChange={(_event, newValue) => {
                onChange(newValue ? newValue.value : "");
              }}
              options={options}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              filterOptions={(options, state) => {
                return filterOptions(options, state, visibleOtherOption);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  required={field.required}
                  label={field.label}
                  placeholder={field.placeholder}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 200,
                    overflow: "auto",
                  },
                },
              }}
            />
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
          </StyledFormControl>
        )}
      />
    </Box>
  );
};

export default SelectField;
